<template>
  <FlipCard
    title="Probability"
    color="white"
    font="primary"
    icon="fa-regular fa-triangle"
    iconColor="primary"
    :value="flipped"
    @click.stop="click"
  >
    <template v-slot:front>
      <BigNumber
        v-if="assignedValue"
        title=""
        subtitle="percent"
        :value="value.value"
        valueFont="display-4 primary--text"
        subtitleFont="subtitle-2 primary--text"
        @click="click"
      ></BigNumber>
      <v-icon center v-else size="90" color="primary">fa-solid fa-empty-set</v-icon>
      <div v-if="subtitle" class="pt-4 subtitle-2 primary--text">
        <span>{{ subtitle.name }}: </span>
        <template v-if="subtitle.value !== null">{{ subtitle.value }}%</template>
        <v-icon small v-else color="primary">fa-regular fa-empty-set</v-icon>
      </div>
    </template>
    <template v-slot:frontAction>
      <v-btn v-if="!readonly && !isActive" icon>
        <v-icon color="secondary">fa-plus</v-icon>
      </v-btn>
    </template>
    <template v-slot:back>
      <v-form
        class="pt-0"
        ref="form"
        :readonly="readonly"
        @click.native.stop
        @mousedown.native.stop
        @keydown.native.stop
      >
        <v-row dense no-gutters>
          <v-col cols="12">
            <v-list-item-group>
              <v-select
                :items="failureDistributions"
                :value="value.failureDistribution"
                @input="valuePropertyInput('failureDistribution', ...arguments)"
                label="Failure distribution type"
                hide-details="auto"
              >
                <template v-slot:prepend>
                  <v-icon class="prepend-icon" small color="primary">fa-regular fa-function</v-icon>
                </template>
              </v-select>
            </v-list-item-group>
          </v-col>
          <template
            v-if="
              value.failureDistribution &&
              value.failureDistributionParameters &&
              value.failureDistribution === 'weibull'
            "
          >
            <v-col cols="4">
              <v-text-field
                ref="shape"
                label="Shape (Beta)"
                :value="value.failureDistributionParameters.shape"
                @input="failureDistributionParametersPropertyInput('shape', ...arguments)"
                :rules="isActive ? [rules.required, rules.isPositive] : []"
                class="number-without-spinners"
                type="number"
                min="0"
                hide-details="auto"
              >
                <template v-slot:prepend>
                  <v-icon class="prepend-icon" small color="primary">fa-regular fa-wave-sine</v-icon>
                </template>
              </v-text-field>
            </v-col>
            <v-col cols="8">
              <DurationEdit
                ref="charLife"
                key="1"
                label="Characteristic life (Eta)"
                :value="value.failureDistributionParameters.scale"
                @input="failureDistributionParametersPropertyInput('scale', ...arguments)"
                :duration-rules="isActive ? [rules.required, rules.charLifeInRange] : []"
                :readonly="readonly"
                :blur="blur"
                defaultDurationType="years"
              ></DurationEdit>
            </v-col>
            <v-col cols="12">
              <v-text-field
                label="Probability"
                :value="value.value"
                class="number-without-spinners"
                type="number"
                min="0"
                max="100"
                hide-details="auto"
                disabled
              >
                <template v-slot:prepend>
                  <v-icon class="prepend-icon" small color="primary">fa-regular fa-triangle</v-icon>
                </template>
                <template v-slot:append>
                  <v-icon small color="primary">fa-regular fa-percent</v-icon>
                </template>
              </v-text-field>
            </v-col>
          </template>
          <template v-else-if="value.failureDistribution === 'none'">
            <v-col cols="12">
              <v-text-field
                label="Probability"
                :value="value.value"
                @input="valuePropertyInput('value', ...arguments)"
                :rules="
                  isActive && !noneProbabilityReadonly ? [rules.isInteger, rules.required, rules.probabilityRange] : []
                "
                class="number-without-spinners"
                type="number"
                min="0"
                max="100"
                hide-details="auto"
                :readonly="noneProbabilityReadonly"
                :hint="noneProbabilityHint"
                persistent-hint
              >
                <template v-slot:prepend>
                  <v-icon class="prepend-icon" small color="primary">fa-regular fa-triangle</v-icon>
                </template>
                <template v-slot:append>
                  <v-icon small color="primary">fa-regular fa-percent</v-icon>
                </template>
              </v-text-field>
            </v-col>
            <v-col cols="12">
              <DurationEdit
                key="2"
                label="Calculated characteristic life"
                :value="value.failureDistributionParameters ? value.failureDistributionParameters.scale : null"
                disabled
              ></DurationEdit>
            </v-col>
          </template>
          <v-col v-if="!hideMtbf" class="measured-mtbf-area">
            <span class="measured-mtbf-area__label">Measured MTBF</span>
            <MeasuredMtbf
              class="text--primary"
              :value="failureData ? failureData.mtbf : null"
              :previousMtbf="failureData ? failureData.previousMtbf : null"
            ></MeasuredMtbf>
          </v-col>
        </v-row>
      </v-form>
    </template>
    <template v-slot:backAction v-if="!readonly">
      <v-btn v-if="isActive" icon @click.stop="remove">
        <v-icon color="primary">fa-trash-can</v-icon>
      </v-btn>
    </template>
  </FlipCard>
</template>

<script>
import { BigNumber, DurationEdit, FlipCard, Rules, Time } from '@itus/ui.common'
import { ProbabilityModel, probabilityLifeCycleState } from './probability-model'
import { ProbabilityCalculator, NoDistribution } from './distribution'
import MeasuredMtbf from './MeasuredMtbf.vue'

export default {
  name: 'ProbabilityCard',
  props: {
    value: {
      type: Object,
      description: 'ProbabilityModel object'
    },
    failureModeProbability: {
      type: Number,
      description: 'Falure Mode driving probability.'
    },
    failureData: {
      type: Object,
      description: 'Failure summary data for the asset, including MTBF'
    },
    hideMtbf: {
      type: Boolean,
      description: 'Whether to hide the measured MTBF field on the probability card.'
    },
    startDate: {
      type: String,
      description: 'Start date to use for probability calcs'
    },
    missionTime: {
      type: String,
      description: 'Mission time to use for probability calcs'
    },
    inAnalysisContext: Boolean,
    analysisProbabilityValue: {
      type: Number,
      description: 'Calculated analysis probability.'
    },
    timeZone: {
      type: String,
      description: 'Time zone to use for probability calcs'
    },
    readonly: Boolean,
    blur: Boolean
  },
  data() {
    return {
      failureDistributions: [
        { value: 'none', text: 'None' },
        { value: 'weibull', text: 'Weibull' }
      ],
      rules: {
        isInteger: Rules.isInteger,
        required: Rules.required,
        probabilityRange: Rules.numberInRange.bind(null, 0, 100),
        charLifeInRange: Rules.durationValueInRange.bind(null, 0, 2147483647),
        isPositive: Rules.isPositive
      },
      flipped: false
    }
  },
  components: {
    FlipCard,
    BigNumber,
    DurationEdit,
    MeasuredMtbf
  },
  computed: {
    isActive() {
      return (
        this.value &&
        this.value.lifeCycleState !== probabilityLifeCycleState.notDefined &&
        this.value.lifeCycleState !== probabilityLifeCycleState.removed
      )
    },
    assignedValue() {
      return this.isActive && this.value.value !== null && this.value.value !== undefined
    },
    assignedFailureModeValue() {
      return this.failureModeProbability !== null && this.failureModeProbability !== undefined
    },
    assignedAnalysisValue() {
      return this.analysisProbabilityValue !== null && this.analysisProbabilityValue !== undefined
    },
    noneProbabilityReadonly() {
      return (
        this.readonly ||
        this.missionTime === null ||
        this.missionTime === undefined ||
        this.startDate === null ||
        this.startDate === undefined
      )
    },
    noneProbabilityHint() {
      return this.noneProbabilityReadonly ? 'Probability % needs a start date and mission time' : undefined
    },
    missionProps() {
      return { startDate: this.startDate, missionTime: this.missionTime }
    },
    subtitle() {
      const analysisName = 'Analysis Probability'
      const failureModeName = 'Failure Mode Probability'
      if (this.inAnalysisContext) {
        if (!this.value.twinId) {
          return null
        }
        if (this.assignedAnalysisValue) {
          return { name: analysisName, value: this.analysisProbabilityValue }
        } else if (this.assignedFailureModeValue) {
          return { name: failureModeName, value: this.failureModeProbability }
        }
        return { name: analysisName, value: null }
      } else if (!this.assignedValue && this.assignedFailureModeValue) {
        return { name: failureModeName, value: this.failureModeProbability }
      }
      return null
    }
  },
  watch: {
    missionProps: {
      handler() {
        if (this.value) {
          let updatedValue = new ProbabilityModel(this.value)
          ProbabilityCalculator.calculate(this.timeZone, this.startDate, this.missionTime, updatedValue)
          this.$emit('input', updatedValue)
        }
      },
      deep: true
    }
  },
  methods: {
    click() {
      if (this.flipped && this.isActive && !this.$refs.form.validate()) {
        return
      }
      if (!this.flipped) {
        this.$refs.form.resetValidation()
      }
      this.$nextTick(() => (this.flipped = !this.flipped))
    },
    valuePropertyInput(property, newPropertyValue) {
      if (this.value[property] === newPropertyValue) {
        return
      }

      let newData = Object.assign({}, this.value)
      newData[property] = newPropertyValue
      if (property === 'failureDistribution' && newData.failureDistributionParameters) {
        if (newData.failureDistribution === 'weibull') {
          newData.failureDistributionParameters.type = 'cdf'
          if (newData.failureDistributionParameters.scale) {
            //  truncate an existing mtbf to an integer since we don't support decimal characteristic life in Weibull
            newData.failureDistributionParameters.scale = Time.truncateDuration(
              newData.failureDistributionParameters.scale
            )
          }
        } else if (newData.failureDistribution === 'none') {
          newData.failureDistributionParameters.type = 'none'
        }
      }

      if (newData.failureDistribution === 'none') {
        if (
          newData.value !== '' &&
          newData.value !== null &&
          newData.value !== undefined &&
          this.missionTime &&
          this.startDate
        ) {
          const distributionCalc = new NoDistribution(this.timeZone, this.startDate, this.missionTime, newData.value)
          const calculatedMtbf = distributionCalc.calculateMtbfAtMissionDate()
          newData.failureDistributionParameters = calculatedMtbf ? { type: 'none', scale: calculatedMtbf } : null
        } else {
          newData.failureDistributionParameters = null
        }
      }

      this.setLifeCycleState(newData)

      let newValue = new ProbabilityModel(newData)
      this.$emit('input', newValue)
    },
    failureDistributionParametersPropertyInput(property, newPropertyValue) {
      if (
        !this.value.failureDistributionParameters ||
        this.value.failureDistributionParameters[property] === newPropertyValue
      ) {
        return
      }

      let newData = Object.assign({}, this.value)
      newData.failureDistributionParameters[property] = newPropertyValue
      this.setLifeCycleState(newData)

      let newValue = new ProbabilityModel(newData)

      this.$emit('input', newValue)
    },
    setLifeCycleState(newValue) {
      if (!newValue.lifeCycleState) {
        return
      }
      if (newValue.lifeCycleState === probabilityLifeCycleState.notDefined) {
        newValue.lifeCycleState = probabilityLifeCycleState.new
      } else if (newValue.lifeCycleState === probabilityLifeCycleState.removed) {
        newValue.lifeCycleState = probabilityLifeCycleState.edit
      }
    },
    async remove() {
      this.flipped = false
      let newLifeCycleState = probabilityLifeCycleState.edit
      if (this.value.lifeCycleState === probabilityLifeCycleState.new) {
        newLifeCycleState = probabilityLifeCycleState.notDefined
      } else if (!this.value.lifeCycleState) {
        newLifeCycleState = probabilityLifeCycleState.removed
      }

      let removedProbability = new ProbabilityModel({
        id: this.value.id,
        assetId: this.value.assetId,
        twinId: this.value.twinId,
        failureModeId: this.value.failureModeId,
        lifeCycleState: newLifeCycleState
      })
      this.$emit('input', removedProbability)
    },
    validate() {
      if (!this.isActive) {
        return true
      }

      const valid = this.$refs.form.validate()
      if (!valid && !this.flipped) {
        this.flipped = true
      }
      return valid
    }
  }
}
</script>

<style lang="scss" scoped>
.number-without-spinners ::v-deep input[type='number'] {
  -moz-appearance: textfield;
}
.number-without-spinners ::v-deep input::-webkit-outer-spin-button,
.number-without-spinners ::v-deep input::-webkit-inner-spin-button {
  -webkit-appearance: none;
}

.prepend-icon {
  min-width: 20px;
}

.measured-mtbf-area {
  margin-top: 2px;
  text-align: center;
  .measured-mtbf-area__label {
    margin-right: 8px;
  }
}
</style>
